import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import {
    AiPrompt,
    deleteAiPrompt,
    getAiPrompts,
    PromptType,
    updateAiPrompt
} from "@/services/supabase/ai-prompts";
import { Loader2, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { PromptCard } from "./prompt-card";
import { PromptDialog } from "./prompt-dialog";



interface AiPromptsManagerProps {
    // No longer need scope props as we fetch all
}

export function AiPromptsManager({ }: AiPromptsManagerProps) {
    const [prompts, setPrompts] = useState<AiPrompt[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState<AiPrompt | null>(null);
    const { toast } = useToast();
    const { projectId } = useParams();

    const loadPrompts = async () => {
        try {
            setIsLoading(true);
            // Fetch ALL prompts (undefined scope)
            const data = await getAiPrompts();
            setPrompts(data);
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to load prompts",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        loadPrompts();
    }, []);

    const filteredPrompts = useMemo(() => {
        if (!searchQuery) return prompts;
        const lowerQuery = searchQuery.toLowerCase();
        return prompts.filter(p =>
            p.name.toLowerCase().includes(lowerQuery) ||
            p.content.toLowerCase().includes(lowerQuery)
        );
    }, [prompts, searchQuery]);

    const handleDelete = async (id: number) => {
        try {
            await deleteAiPrompt(id);
            setPrompts(prompts.filter((p) => p.id !== id));
            toast({
                title: "Success",
                description: "Prompt deleted successfully",
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to delete prompt",
                variant: "destructive",
            });
        }
    };

    const handleToggleActive = async (prompt: AiPrompt, checked: boolean) => {
        try {
            // Optimistic update
            setPrompts(prompts.map(p => p.id === prompt.id ? { ...p, is_active: checked } : p));

            await updateAiPrompt({
                id: prompt.id,
                is_active: checked
            });
        } catch (error) {
            // Revert on error
            setPrompts(prompts.map(p => p.id === prompt.id ? { ...p, is_active: !checked } : p));
            toast({
                title: "Error",
                description: "Failed to update status",
                variant: "destructive",
            });
        }
    };

    const writingActions = useMemo(() =>
        filteredPrompts.filter(p => p.type === PromptType.WritingAction),
        [filteredPrompts]);

    const systemInstructions = useMemo(() =>
        filteredPrompts.filter(p => p.type === PromptType.SystemInstruction),
        [filteredPrompts]);

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex items-center justify-between gap-4 pb-6 border-b border-border/40">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search prompts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 h-10 rounded-xl bg-muted/30 border-muted-foreground/10 focus:bg-background transition-all"
                    />
                </div>
                <Button
                    onClick={() => { setEditingPrompt(null); setIsDialogOpen(true); }}
                    className="rounded-xl shadow-sm hover:shadow-md transition-all active:scale-95 h-10 px-6 font-medium"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    New Prompt
                </Button>
            </div>

            {isLoading ? (
                <div className="flex justify-center p-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            ) : filteredPrompts.length === 0 ? (
                <div className="text-center p-16 border border-dashed border-border/60 rounded-xl text-muted-foreground bg-muted/5">
                    {searchQuery ? "No prompts match your search." : "No prompts found. Create one to get started."}
                </div>
            ) : (
                <div className="space-y-10">
                    {/* Writing Actions Section */}
                    {writingActions.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 px-1">
                                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Writing Actions</h3>
                                <div className="h-px bg-border/40 flex-1" />
                            </div>
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2">
                                {writingActions.map(prompt => (
                                    <PromptCard
                                        key={prompt.id}
                                        prompt={prompt}
                                        onEdit={() => { setEditingPrompt(prompt); setIsDialogOpen(true); }}
                                        onDelete={() => handleDelete(prompt.id)}
                                        onToggleActive={(checked) => handleToggleActive(prompt, checked)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* System Instructions Section */}
                    {systemInstructions.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 px-1">
                                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">System Instructions</h3>
                                <div className="h-px bg-border/40 flex-1" />
                            </div>
                            <div className="grid gap-3">
                                {systemInstructions.map(prompt => (
                                    <PromptCard
                                        key={prompt.id}
                                        prompt={prompt}
                                        onEdit={() => { setEditingPrompt(prompt); setIsDialogOpen(true); }}
                                        onDelete={() => handleDelete(prompt.id)}
                                        onToggleActive={(checked) => handleToggleActive(prompt, checked)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <PromptDialog
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                projectId={projectId}
                prompt={editingPrompt}
                onSuccess={() => {
                    setIsDialogOpen(false);
                    loadPrompts();
                }}
                onDelete={handleDelete}
            />
        </div>
    );
}


